/**
 * Payer Connections — open Stedi provider tasks (GDN-3992).
 *
 * Pure functions. A PROVIDER_ACTION_REQUIRED row with an open task shows the
 * task's instructions in place of canned copy; nothing here touches the
 * fold's state, counts or sort. The backend already keeps only open, PROVIDER-owned tasks, lowest
 * rank first, so this module never re-filters by party or re-sorts.
 */

import { STEDI_TASK_LINK_LABEL } from './copy';
import type { EnrollmentStatus, EnrollmentTask, OpenTaskView } from './types';

/**
 * The only status whose open task the row shows. Stedi's guidance (GDN-4026):
 * the enrollment status, not the task list, says when the provider must act.
 * An open provider task under STEDI_ACTION_REQUIRED or PROVISIONING may
 * depend on a step Stedi has not finished, so showing it would ask the
 * clinic to act early; finished enrollments (LIVE, CANCELED, REJECTED) can
 * still list stale open tasks that mean nothing.
 */
export const TASK_COPY_STATUSES: readonly EnrollmentStatus[] = ['PROVIDER_ACTION_REQUIRED'];

/**
 * The task a rail's copy comes from: the first (lowest-rank) open task whose
 * definition we understand and that has instructions to show. An
 * `unsupported` task, or a manual one with no instructions, falls through —
 * to the next task, then to status_message/rejection_reason/canned copy.
 */
export function pickOpenTask(
  status: EnrollmentStatus,
  tasks: EnrollmentTask[] | undefined,
): OpenTaskView | undefined {
  if (!tasks || tasks.length === 0) return undefined;
  if (!TASK_COPY_STATUSES.includes(status)) return undefined;
  const task = tasks.find(
    candidate => candidate.definition_kind === 'manual_task' && !!candidate.instructions?.trim(),
  );
  if (!task?.instructions) return undefined;
  return {
    taskId: task.task_id,
    instructions: task.instructions,
    links: task.links ?? [],
    remainingCount: tasks.length - 1,
  };
}

/** Where Stedi's own task pages live; a bare address without a scheme still opens over https. */
const STEDI_HOST_PATTERN = /^enrollments\.stedi\.com(?:[/?#]|$)/;

/** http(s) URLs, or a bare enrollments.stedi.com address, up to the next whitespace or closing bracket. */
const LINK_PATTERN = /https?:\/\/[^\s<>"')\]]+|\benrollments\.stedi\.com(?:[/?#][^\s<>"')\]]*)?/g;

/** Punctuation that ends a sentence rather than a URL. */
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;

function nonEmptyLines(instructions: string): string[] {
  return instructions
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);
}

/** A line that is nothing but a URL is not a title. */
function isBareUrl(line: string): boolean {
  return /^\S+$/.test(line) && (safeHttpUrl(line) !== null || STEDI_HOST_PATTERN.test(line));
}

/**
 * The one-line `whatsHappening` summary for a task: its first non-empty
 * line, trimmed. When the instructions are a single line, or that first
 * line is a bare URL, the whole text — collapsed to one line — is the
 * summary instead.
 */
export function taskSummaryLine(instructions: string): string {
  const lines = nonEmptyLines(instructions);
  if (lines.length === 0) return '';
  if (lines.length === 1 || isBareUrl(lines[0])) return lines.join(' ');
  return lines[0];
}

/**
 * The readable body under the summary: everything after the title line,
 * line breaks kept, leading/trailing blank lines dropped. Empty when the
 * summary already is the whole text, so the row never repeats itself.
 */
export function taskBodyText(instructions: string): string {
  const lines = nonEmptyLines(instructions);
  if (lines.length <= 1 || isBareUrl(lines[0])) return '';
  const rawLines = instructions.split(/\r?\n/);
  const titleIndex = rawLines.findIndex(line => line.trim() === lines[0]);
  return rawLines
    .slice(titleIndex + 1)
    .map(line => line.trim())
    .join('\n')
    .replace(/^\n+|\n+$/g, '');
}

/** The URL when it parses and is http(s); null for every other scheme or non-URL. */
export function safeHttpUrl(candidate: string): string | null {
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:' ? candidate : null;
  } catch {
    return null;
  }
}

export type LinkToken =
  | { kind: 'text'; text: string }
  | { kind: 'link'; text: string; href: string };

/**
 * Whether a link should read as a label rather than its URL: it points at
 * Stedi's task pages and stands alone in the text (its own line, or the
 * tail of "…next steps: <url>"), so nothing around it uses the URL as a
 * noun. A URL inside a sentence ("download the form at <url> and sign it")
 * keeps its text so the sentence still reads.
 */
function isStandaloneStediLink(text: string, start: number, end: number, href: string): boolean {
  if (new URL(href).hostname !== 'enrollments.stedi.com') return false;
  const before = text.slice(0, start);
  const after = text.slice(end).replace(/^[.,;:!?]+/, '');
  const startsRun = /(^|:|\n)[ \t]*$/.test(before);
  const endsRun = /^[ \t]*(\n|$)/.test(after);
  return startsRun && endsRun;
}

/**
 * Split text into plain runs and links, for rendering as text nodes and
 * anchors — never HTML. Only http(s) targets become links; anything else
 * stays text. A standalone link to Stedi's task pages gets a readable label
 * instead of a long raw URL that wraps across the row.
 */
export function linkifyTokens(text: string): LinkToken[] {
  const tokens: LinkToken[] = [];
  let cursor = 0;
  const pushText = (chunk: string) => {
    if (!chunk) return;
    const last = tokens[tokens.length - 1];
    if (last?.kind === 'text') last.text += chunk;
    else tokens.push({ kind: 'text', text: chunk });
  };
  for (const match of text.matchAll(LINK_PATTERN)) {
    const start = match.index ?? 0;
    const raw = match[0].replace(TRAILING_PUNCTUATION, '');
    const href = safeHttpUrl(raw.startsWith('http') ? raw : `https://${raw}`);
    pushText(text.slice(cursor, start));
    if (href) {
      const standalone = isStandaloneStediLink(text, start, start + raw.length, href);
      tokens.push({ kind: 'link', text: standalone ? STEDI_TASK_LINK_LABEL : raw, href });
      cursor = start + raw.length;
    } else {
      pushText(match[0]);
      cursor = start + match[0].length;
    }
  }
  pushText(text.slice(cursor));
  return tokens;
}
